/**
 * Demo Mode switch. Decided once at startup, before any data access, so a
 * Supabase client is never constructed while Demo Mode is active.
 */
import { createDemoRepo } from './demoRepo';
import type { Repo } from './repo';

const KEY = 'financeCockpitDemoMode';

function storage(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}

export function isDemoMode(): boolean {
  const s = storage();
  if (new URLSearchParams(window.location.search).get('demo') === '1') {
    try { s?.setItem(KEY, '1'); } catch { /* still demo for this load */ }
    return true;
  }
  try { return s?.getItem(KEY) === '1'; } catch { return false; }
}

export interface LiveConfig {
  url: string | undefined;
  publishableKey: string | undefined;
}

export type RepoState =
  | { kind: 'ready'; repo: Repo }
  | { kind: 'unconfigured' };

/** Choose the data source. The live module is imported lazily so demo builds never load it. */
export async function loadRepo(config: LiveConfig): Promise<RepoState> {
  if (isDemoMode()) return { kind: 'ready', repo: createDemoRepo() };
  if (!config.url || !config.publishableKey) return { kind: 'unconfigured' };
  const { createSupabaseRepo } = await import('./supabaseRepo');
  return { kind: 'ready', repo: createSupabaseRepo(config.url, config.publishableKey) };
}

/** Sign out of live data first; if that fails, do not switch. Then reload into demo. */
export async function enterDemo(signOutLive: () => Promise<void>): Promise<void> {
  await signOutLive();
  storage()?.setItem(KEY, '1');
  window.location.replace(`${window.location.pathname}?demo=1#/home`);
}

/** Leave demo and land on the sign-in gate. Live data always needs a fresh sign-in. */
export function exitDemo(): void {
  try { storage()?.removeItem(KEY); } catch { /* ignore */ }
  window.location.replace(`${window.location.pathname}#/home`);
}
